import React, { useEffect } from 'react';

export const ParameterHistoryModal = ({
  isOpen,
  onClose,
  data,
  loading = false,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatDate = (dStr) => {
    if (!dStr) return '—';
    try {
      const d = new Date(dStr);
      if (isNaN(d.getTime())) return dStr;
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dStr;
    }
  };

  const currentResult = data?.currentResult;
  const previousResult = data?.previousResult;
  const historyList = data?.history || [];
  const isNumeric = currentResult?.numericValue !== null && currentResult?.numericValue !== undefined;

  // Chart data: chronological order (oldest to newest)
  const chartPoints = [...historyList].reverse();

  // Reference range min and max for chart shading
  let refMin = parseFloat(data?.minReading);
  let refMax = parseFloat(data?.maxReading);
  if (isNaN(refMin) && data?.displayReading) {
    const parts = data.displayReading.split('-');
    if (parts.length === 2) {
      refMin = parseFloat(parts[0].trim());
      refMax = parseFloat(parts[1].trim());
    }
  }

  // Calculate SVG Chart Dimensions & Scales
  const svgWidth = 620;
  const svgHeight = 220;
  const padding = { top: 25, right: 35, bottom: 45, left: 50 };
  const plotWidth = svgWidth - padding.left - padding.right;
  const plotHeight = svgHeight - padding.top - padding.bottom;

  // Find min and max for Y scale
  const numericValues = chartPoints
    .map((p) => p.numericValue)
    .filter((v) => v !== null && v !== undefined && !isNaN(v));

  let minY = Math.min(...numericValues, !isNaN(refMin) ? refMin : Infinity);
  let maxY = Math.max(...numericValues, !isNaN(refMax) ? refMax : -Infinity);

  if (minY === Infinity) minY = 0;
  if (maxY === -Infinity) maxY = 20;

  // Add 15% headroom and floor
  const rangeSpan = maxY - minY || 10;
  const yDomainMin = Math.max(0, Math.floor(minY - rangeSpan * 0.15));
  const yDomainMax = Math.ceil(maxY + rangeSpan * 0.15);

  const getY = (val) => {
    if (val === null || val === undefined || isNaN(val)) return plotHeight / 2;
    const ratio = (val - yDomainMin) / (yDomainMax - yDomainMin || 1);
    return padding.top + plotHeight - ratio * plotHeight;
  };

  const getX = (index) => {
    if (chartPoints.length <= 1) return padding.left + plotWidth / 2;
    return padding.left + (index / (chartPoints.length - 1)) * plotWidth;
  };

  // Build SVG path
  const linePoints = chartPoints
    .map((p, idx) => {
      if (p.numericValue === null || p.numericValue === undefined || isNaN(p.numericValue)) return null;
      return `${getX(idx)},${getY(p.numericValue)}`;
    })
    .filter(Boolean);

  const pathD = linePoints.length > 0 ? `M ${linePoints.join(' L ')}` : '';

  // Reference Range Shaded Band
  const refBandTop = !isNaN(refMax) ? getY(refMax) : null;
  const refBandBottom = !isNaN(refMin) ? getY(refMin) : null;
  const refBandHeight = refBandTop !== null && refBandBottom !== null ? Math.max(2, refBandBottom - refBandTop) : 0;

  // Y-axis grid ticks (4 ticks)
  const yTicks = [
    yDomainMin,
    Math.round(yDomainMin + (yDomainMax - yDomainMin) * 0.33),
    Math.round(yDomainMin + (yDomainMax - yDomainMin) * 0.66),
    yDomainMax,
  ];

  const renderDeltaBadge = () => {
    switch (data?.deltaStatus) {
      case 'DELTA_ALERT':
        return (
          <div className="delta-status-box status-box-alert">
            <span className="delta-status-title">⚠️ Delta Alert</span>
            <span className="delta-status-sub">{data.deltaMessage || 'Significant change from previous result'}</span>
          </div>
        );
      case 'CRITICAL':
        return (
          <div className="delta-status-box status-box-critical">
            <span className="delta-status-title">🚨 Critical Delta</span>
            <span className="delta-status-sub">{data.deltaMessage || 'Critical Panic Value requires clinical review'}</span>
          </div>
        );
      case 'QUALITATIVE_CHANGE':
        return (
          <div className="delta-status-box status-box-alert">
            <span className="delta-status-title">⚡ Qualitative Shift</span>
            <span className="delta-status-sub">{data.deltaMessage || 'Qualitative change detected from previous test'}</span>
          </div>
        );
      case 'NORMAL':
        return (
          <div className="delta-status-box status-box-normal">
            <span className="delta-status-title">✓ Normal</span>
            <span className="delta-status-sub">{data.deltaMessage || 'Within expected variance'}</span>
          </div>
        );
      case 'NO_PREVIOUS':
      default:
        return (
          <div className="delta-status-box status-box-muted">
            <span className="delta-status-title">ℹ️ No History</span>
            <span className="delta-status-sub">No previous result available</span>
          </div>
        );
    }
  };

  const renderStatusPill = (status, flag) => {
    if (status === 'Critical' || flag === 'LL' || flag === 'HH') {
      return <span className="delta-table-badge badge-crit">⚠️ Critical</span>;
    }
    if (status === 'Low' || flag === 'L') {
      return <span className="delta-table-badge badge-low">🔻 Low</span>;
    }
    if (status === 'High' || flag === 'H') {
      return <span className="delta-table-badge badge-high">🔺 High</span>;
    }
    if (status === 'Abnormal' || flag === 'A') {
      return <span className="delta-table-badge badge-abn">⚠️ Abnormal</span>;
    }
    return <span className="delta-table-badge badge-norm">✓ Normal</span>;
  };

  return (
    <div className="path-param-drawer-backdrop" onClick={onClose}>
      <div
        className="path-param-drawer-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div className="path-param-drawer-header">
          <div className="history-header-titles">
            <div className="history-main-title">
              <span className="history-icon-badge">📈</span>
              <h3>Parameter History & Delta Check</h3>
            </div>
            <div className="history-sub-meta">
              <span className="param-title-highlight">{data?.observationName || 'Observation'}</span>
              {data?.labObservationId && <span className="param-code-pill">Code: {data.labObservationId}</span>}
              {data?.investigationName && <span className="test-name-tag">Test: {data.investigationName}</span>}
            </div>
          </div>
          <button type="button" className="path-param-drawer-close" onClick={onClose} title="Close (Esc)">
            ✕
          </button>
        </div>

        {/* Drawer Body */}
        <div className="path-param-drawer-body">
          {loading ? (
            <div className="path-history-loading">
              <div className="path-spinner"></div>
              <p>Fetching historical trend data...</p>
            </div>
          ) : (
            <>
              {/* Patient Meta Strip */}
              <div className="delta-patient-strip">
                <div className="strip-item">
                  <span className="strip-label">Patient:</span>
                  <span className="strip-val fw-bold">{data?.patientName || '—'}</span>
                  {data?.patientId && <span className="strip-uhid">({data.patientId})</span>}
                </div>
                <div className="strip-item">
                  <span className="strip-label">Age / Gender:</span>
                  <span className="strip-val">{data?.ageGender || '—'}</span>
                </div>
                <div className="strip-item">
                  <span className="strip-label">Bio Ref Range:</span>
                  <span className="strip-val range-highlight">
                    {data?.displayReading || (refMin && refMax ? `${refMin} - ${refMax}` : '—')} {data?.unit || ''}
                  </span>
                </div>
              </div>

              {/* 5 Delta Metric Summary Cards */}
              <div className="delta-metrics-grid">
                {/* 1. Current Result */}
                <div className="delta-card card-current">
                  <div className="card-label">Current Result</div>
                  <div className="card-main-val">
                    <span className="val-number">{currentResult?.value || '—'}</span>
                    <span className="val-unit">{data?.unit || ''}</span>
                  </div>
                  <div className="card-sub-date">{formatDate(currentResult?.testDate)}</div>
                </div>

                {/* 2. Previous Result */}
                <div className="delta-card card-previous">
                  <div className="card-label">Previous Result</div>
                  <div className="card-main-val">
                    <span className="val-number">{previousResult?.value || '—'}</span>
                    <span className="val-unit">{previousResult ? data?.unit || '' : ''}</span>
                  </div>
                  <div className="card-sub-date">
                    {previousResult ? formatDate(previousResult.testDate) : 'No prior result'}
                  </div>
                </div>

                {/* 3. Absolute Change */}
                <div className="delta-card card-abs-change">
                  <div className="card-label">Absolute Change</div>
                  <div className="card-main-val">
                    <span
                      className={`val-number ${
                        data?.absoluteChange !== null && data?.absoluteChange !== undefined
                          ? data.absoluteChange > 0
                            ? 'change-pos'
                            : data.absoluteChange < 0
                            ? 'change-neg'
                            : ''
                          : ''
                      }`}
                    >
                      {data?.absoluteChange !== null && data?.absoluteChange !== undefined
                        ? (data.absoluteChange > 0 ? `+${data.absoluteChange}` : data.absoluteChange)
                        : '—'}
                    </span>
                    {data?.absoluteChange !== null && data?.absoluteChange !== undefined && (
                      <span className="val-unit">{data?.unit || ''}</span>
                    )}
                  </div>
                  <div className="card-sub-date">Current - Previous</div>
                </div>

                {/* 4. Percentage Change */}
                <div className="delta-card card-pct-change">
                  <div className="card-label">Percentage Change</div>
                  <div className="card-main-val">
                    <span
                      className={`val-number ${
                        data?.percentageChange !== null && data?.percentageChange !== undefined
                          ? data.percentageChange > 0
                            ? 'change-pos'
                            : data.percentageChange < 0
                            ? 'change-neg'
                            : ''
                          : ''
                      }`}
                    >
                      {data?.percentageChange !== null && data?.percentageChange !== undefined
                        ? (data.percentageChange > 0 ? `+${data.percentageChange}%` : `${data.percentageChange}%`)
                        : '—'}
                    </span>
                  </div>
                  <div className="card-sub-date">Relative variance</div>
                </div>

                {/* 5. Delta Status */}
                <div className="delta-card card-status">
                  <div className="card-label">Delta Status</div>
                  {renderDeltaBadge()}
                </div>
              </div>

              {/* Parameter Trend Line Chart */}
              <div className="delta-chart-section">
                <div className="chart-header">
                  <h4>{data?.observationName || 'Parameter'} Trend (Last 5 Results)</h4>
                  <div className="chart-legend">
                    <span className="legend-item">
                      <span className="legend-dot dot-result"></span> Result
                    </span>
                    {!isNaN(refMin) && !isNaN(refMax) && (
                      <span className="legend-item">
                        <span className="legend-band"></span> Ref Range ({refMin} - {refMax} {data?.unit})
                      </span>
                    )}
                  </div>
                </div>

                {numericValues.length > 0 ? (
                  <div className="delta-chart-container">
                    <svg
                      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                      className="delta-trend-svg"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <defs>
                        <linearGradient id="trendGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#1e88e5" stopOpacity="0.25" />
                          <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.02" />
                        </linearGradient>
                      </defs>

                      {/* Reference Range Shaded Area */}
                      {refBandTop !== null && refBandBottom !== null && (
                        <rect
                          x={padding.left}
                          y={refBandTop}
                          width={plotWidth}
                          height={refBandHeight}
                          fill="rgba(46, 204, 113, 0.14)"
                          stroke="rgba(46, 204, 113, 0.35)"
                          strokeDasharray="4 3"
                        />
                      )}

                      {/* Horizontal Y-Axis Grid Lines */}
                      {yTicks.map((val, i) => {
                        const y = getY(val);
                        return (
                          <g key={i}>
                            <line
                              x1={padding.left}
                              y1={y}
                              x2={padding.left + plotWidth}
                              y2={y}
                              stroke="#e2e8f0"
                              strokeDasharray="3 3"
                            />
                            <text
                              x={padding.left - 8}
                              y={y + 4}
                              fontSize="10"
                              fill="#64748b"
                              textAnchor="end"
                            >
                              {val}
                            </text>
                          </g>
                        );
                      })}

                      {/* Trend Line */}
                      {pathD && (
                        <path
                          d={pathD}
                          fill="none"
                          stroke="#1e88e5"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}

                      {/* Data Points and Value Callouts */}
                      {chartPoints.map((pt, idx) => {
                        if (pt.numericValue === null || pt.numericValue === undefined || isNaN(pt.numericValue)) {
                          return null;
                        }
                        const cx = getX(idx);
                        const cy = getY(pt.numericValue);
                        const isCurrent = pt.isCurrent;

                        return (
                          <g key={pt.testId || idx} className="chart-point-group">
                            {/* Outer halo for current point */}
                            {isCurrent && (
                              <circle
                                cx={cx}
                                cy={cy}
                                r="8"
                                fill="none"
                                stroke="#1e88e5"
                                strokeWidth="2"
                                strokeDasharray="2 2"
                                className="pulsing-halo"
                              />
                            )}

                            {/* Point circle */}
                            <circle
                              cx={cx}
                              cy={cy}
                              r={isCurrent ? '5.5' : '4.5'}
                              fill={isCurrent ? '#d32f2f' : '#1e88e5'}
                              stroke="#ffffff"
                              strokeWidth="2"
                            />

                            {/* Value Label */}
                            <text
                              x={cx}
                              y={cy - 9}
                              fontSize="11"
                              fontWeight={isCurrent ? 'bold' : '600'}
                              fill={isCurrent ? '#b71c1c' : '#0f172a'}
                              textAnchor="middle"
                            >
                              {pt.value}
                            </text>

                            {/* Date Label on X Axis */}
                            <text
                              x={cx}
                              y={padding.top + plotHeight + 20}
                              fontSize="10"
                              fill="#475569"
                              textAnchor="middle"
                            >
                              {formatDate(pt.testDate)}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                ) : (
                  <div className="delta-chart-empty">
                    <p>No numeric historical points available to plot trend graph.</p>
                  </div>
                )}
              </div>

              {/* Historical Results Table */}
              <div className="delta-table-section">
                <h4>Last 5 Historical Results</h4>
                <table className="delta-history-table">
                  <thead>
                    <tr>
                      <th style={{ width: '45px' }}>#</th>
                      <th style={{ width: '22%' }}>Date</th>
                      <th style={{ width: '18%' }}>Result</th>
                      <th style={{ width: '12%' }}>Unit</th>
                      <th style={{ width: '26%' }}>Reference Range</th>
                      <th style={{ width: '18%' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyList.length > 0 ? (
                      historyList.map((item, index) => (
                        <tr
                          key={item.testId || index}
                          className={item.isCurrent ? 'row-current-item' : ''}
                        >
                          <td>
                            <span className="row-seq">
                              {index + 1}
                              {item.isCurrent && <span className="curr-tag">Current</span>}
                            </span>
                          </td>
                          <td>
                            <span className="date-main">{formatDate(item.testDate)}</span>
                            {item.ledgerTransactionNo && (
                              <span className="date-sub">{item.ledgerTransactionNo}</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`result-val-text ${
                                item.isCritical
                                  ? 'val-critical'
                                  : item.flag === 'H' || item.flag === 'L'
                                  ? 'val-abnormal'
                                  : 'val-normal'
                              }`}
                            >
                              {item.value}
                            </span>
                          </td>
                          <td>
                            <span className="unit-text">{item.unit || data?.unit || '—'}</span>
                          </td>
                          <td>
                            <span className="ref-range-text">
                              {item.displayReading && item.displayReading !== 'Numeric'
                                ? item.displayReading
                                : data?.displayReading || '—'}
                            </span>
                          </td>
                          <td>{renderStatusPill(item.status, item.flag)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="table-empty-row">
                          No previous historical records found for this parameter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Clinical Interpretation Disclaimer Note */}
              <div className="delta-disclaimer-box">
                <span className="disclaimer-icon">ℹ️</span>
                <span className="disclaimer-text">
                  <strong>Clinical Note:</strong> Delta Check evaluates relative parameter variance between visits.
                  Results should always be interpreted in conjunction with the patient's complete clinical picture,
                  medications, and diagnostic history.
                </span>
              </div>
            </>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="path-param-drawer-footer">
          <button type="button" className="path-btn path-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParameterHistoryModal;
